import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  Modal,
  ActivityIndicator,
  Platform,
  Keyboard,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';
import SearchBar from './SearchBar';

// Conditionally import expo-location only on native platforms
let Location: typeof import('expo-location') | null = null;
if (Platform.OS !== 'web') {
  try {
    Location = require('expo-location');
  } catch (e) {
    console.warn('expo-location not available:', e);
  }
}

interface LocationOption {
  display_name: string;
  lat: string;
  lon: string;
  place_id: number;
}

interface LocationPickerProps {
  value: string;
  onChange: (location: string) => void;
  placeholder?: string;
}

const LocationPicker: React.FC<LocationPickerProps> = ({
  value,
  onChange,
  placeholder = 'Search for a location or enter manually',
}) => {
  const { colors } = useTheme();
  const [searchQuery, setSearchQuery] = useState(value || '');
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (value) {
      setSearchQuery(value);
    }
  }, [value]);
  const [searchResults, setSearchResults] = useState<LocationOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [, setSelectedLocation] = useState<LocationOption | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const searchLocations = async (query: string) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const encodedQuery = encodeURIComponent(query);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodedQuery}&limit=5&addressdetails=1`
      );

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      setSearchResults(data || []);
    } catch (err) {
      console.error('Location search error:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (text: string) => {
    setSearchQuery(text);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      searchLocations(text);
    }, 500);
  };

  const handleSelectLocation = (location: LocationOption) => {
    setSelectedLocation(location);
    onChange(location.display_name);
    setSearchQuery(location.display_name);
    setShowModal(false);
    Keyboard.dismiss();
  };

  const handleManualEntry = () => {
    onChange(searchQuery);
    setShowModal(false);
    Keyboard.dismiss();
  };

  const handleClear = () => {
    setSearchQuery('');
    onChange('');
    setSearchResults([]);
    setSelectedLocation(null);
  };

  const handleUseCurrentLocation = async () => {
    // Check if location is available (not on web)
    if (Platform.OS === 'web' || !Location) {
      Alert.alert(
        'Not Available',
        'Current location is only available on mobile devices. Please search for a location or enter manually.',
        [{ text: 'OK' }]
      );
      return;
    }

    try {
      // Request location permissions
      const { status } = await Location.requestForegroundPermissionsAsync();

      if (status !== 'granted') {
        Alert.alert(
          'Permission Denied',
          'Location permission is required to use your current location.',
          [{ text: 'OK' }]
        );
        return;
      }

      // Get current location
      setLoading(true);
      const location = await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy.Balanced,
      });

      const { latitude, longitude } = location.coords;

      // Reverse geocode to get address
      const reverseGeocode = await Location.reverseGeocodeAsync({
        latitude,
        longitude,
      });

      if (reverseGeocode && reverseGeocode.length > 0) {
        const address = reverseGeocode[0];
        const locationName = [
          address.name,
          address.street,
          address.city,
          address.region,
          address.country,
        ]
          .filter(Boolean)
          .join(', ');

        setSearchQuery(locationName);
        onChange(locationName);
        setShowModal(false);
        Keyboard.dismiss();
      } else {
        // Fallback to coordinates
        const locationName = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
        setSearchQuery(locationName);
        onChange(locationName);
        setShowModal(false);
        Keyboard.dismiss();
      }
    } catch (error) {
      console.error('Error getting current location:', error);
      Alert.alert(
        'Error',
        'Could not get your current location. Please try again or search manually.',
        [{ text: 'OK' }]
      );
    } finally {
      setLoading(false);
    }
  };

  const renderLocationItem = ({ item }: { item: LocationOption }) => (
    <TouchableOpacity
      style={[styles.locationItem, { backgroundColor: colors.card }]}
      onPress={() => handleSelectLocation(item)}
    >
      <Ionicons name="location-outline" size={20} color={colors.primary} />
      <View style={styles.locationItemText}>
        <Text
          style={[styles.locationName, { color: colors.text }]}
          numberOfLines={1}
        >
          {item.display_name.split(',')[0]}
        </Text>
        <Text
          style={[styles.locationAddress, { color: colors.secondary }]}
          numberOfLines={1}
        >
          {item.display_name.split(',').slice(1).join(',').trim()}
        </Text>
      </View>
      <Ionicons name="chevron-forward" size={16} color={colors.secondary} />
    </TouchableOpacity>
  );

  return (
    <View>
      <TouchableOpacity
        style={[
          styles.inputContainer,
          { backgroundColor: colors.background, borderColor: colors.border },
        ]}
        onPress={() => setShowModal(true)}
      >
        <Ionicons name="location-outline" size={20} color={colors.primary} />
        <Text
          style={[
            styles.inputText,
            { color: value ? colors.text : colors.secondary },
          ]}
          numberOfLines={1}
        >
          {value || placeholder}
        </Text>
        {value ? (
          <TouchableOpacity
            onPress={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            style={styles.clearButton}
          >
            <Ionicons name="close-circle" size={20} color={colors.secondary} />
          </TouchableOpacity>
        ) : (
          <Ionicons name="chevron-down" size={20} color={colors.secondary} />
        )}
      </TouchableOpacity>

      <Modal
        visible={showModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowModal(false)}
      >
        <View
          style={[
            styles.modalContainer,
            { backgroundColor: colors.background },
          ]}
        >
          {/* Header */}
          <View
            style={[
              styles.modalHeader,
              {
                backgroundColor: colors.card,
                borderBottomColor: colors.border,
              },
            ]}
          >
            <TouchableOpacity
              onPress={() => {
                setShowModal(false);
              }}
              style={styles.closeButton}
            >
              <Ionicons name="close" size={24} color={colors.text} />
            </TouchableOpacity>
            <Text style={[styles.modalTitle, { color: colors.text }]}>
              Add Location
            </Text>
            <View style={styles.placeholder} />
          </View>

          {/* Search Bar */}
          <View
            style={[styles.searchContainer, { backgroundColor: colors.card }]}
          >
            <SearchBar
              value={searchQuery}
              onChangeText={handleSearchChange}
              placeholder="Search for a location..."
              autoFocus
              containerStyle={{ margin: 0, padding: 0 }}
              style={{ backgroundColor: colors.background }}
            />
            {loading && (
              <View style={styles.loadingIndicator}>
                <ActivityIndicator size="small" color={colors.primary} />
              </View>
            )}
          </View>

          {/* Action Buttons */}
          {Platform.OS !== 'web' && Location && (
            <View
              style={[styles.actionButtons, { backgroundColor: colors.card }]}
            >
              <TouchableOpacity
                style={[
                  styles.actionButton,
                  { backgroundColor: colors.primary + '15' },
                ]}
                onPress={handleUseCurrentLocation}
                disabled={loading}
              >
                {loading ? (
                  <ActivityIndicator size="small" color={colors.primary} />
                ) : (
                  <Ionicons
                    name="navigate-outline"
                    size={20}
                    color={colors.primary}
                  />
                )}
                <Text
                  style={[styles.actionButtonText, { color: colors.primary }]}
                >
                  Use Current Location
                </Text>
              </TouchableOpacity>
            </View>
          )}
          {searchQuery.length > 0 && searchResults.length === 0 && (
            <TouchableOpacity
              style={[
                styles.manualEntryButton,
                { backgroundColor: colors.card },
              ]}
              onPress={handleManualEntry}
            >
              <Ionicons
                name="create-outline"
                size={20}
                color={colors.primary}
              />
              <View style={styles.manualEntryText}>
                <Text style={[styles.manualEntryLabel, { color: colors.text }]}>
                  Use as entered
                </Text>
                <Text
                  style={[styles.manualEntryValue, { color: colors.secondary }]}
                  numberOfLines={1}
                >
                  {searchQuery}
                </Text>
              </View>
              <Ionicons
                name="chevron-forward"
                size={16}
                color={colors.secondary}
              />
            </TouchableOpacity>
          )}

          {/* Search Results */}
          {searchResults.length > 0 && (
            <FlatList
              data={searchResults}
              renderItem={renderLocationItem}
              keyExtractor={(item) => item.place_id.toString()}
              style={styles.resultsList}
              contentContainerStyle={styles.resultsContent}
              keyboardShouldPersistTaps="handled"
            />
          )}

          {/* Empty State */}
          {!loading &&
            searchQuery.length >= 2 &&
            searchResults.length === 0 && (
              <View style={styles.emptyState}>
                <Ionicons
                  name="location-outline"
                  size={48}
                  color={colors.secondary}
                />
                <Text style={[styles.emptyText, { color: colors.secondary }]}>
                  No locations found
                </Text>
                <Text
                  style={[styles.emptySubtext, { color: colors.secondary }]}
                >
                  Try a different search or enter manually
                </Text>
              </View>
            )}

          {/* Initial State */}
          {searchQuery.length < 2 && (
            <View style={styles.emptyState}>
              <Ionicons
                name="search-outline"
                size={48}
                color={colors.secondary}
              />
              <Text style={[styles.emptyText, { color: colors.secondary }]}>
                Search for a location
              </Text>
              <Text style={[styles.emptySubtext, { color: colors.secondary }]}>
                Start typing to search or enter manually
              </Text>
            </View>
          )}
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    gap: 8,
  },
  inputText: {
    flex: 1,
    fontSize: 14,
  },
  clearButton: {
    padding: 4,
  },
  modalContainer: {
    flex: 1,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  closeButton: {
    padding: 4,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  placeholder: {
    width: 32,
  },
  searchContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    margin: 16,
  },
  loadingIndicator: {
    position: 'absolute',
    right: 24,
    top: '50%',
    transform: [{ translateY: -10 }],
  },
  actionButtons: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 8,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  resultsList: {
    flex: 1,
  },
  resultsContent: {
    padding: 16,
  },
  locationItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
    gap: 12,
  },
  locationItemText: {
    flex: 1,
  },
  locationName: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 2,
  },
  locationAddress: {
    fontSize: 12,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  emptyText: {
    marginTop: 16,
    fontSize: 16,
    fontWeight: '600',
  },
  emptySubtext: {
    marginTop: 8,
    fontSize: 14,
    textAlign: 'center',
  },
  manualEntryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    marginHorizontal: 16,
    marginTop: 8,
    borderRadius: 8,
    gap: 12,
  },
  manualEntryText: {
    flex: 1,
  },
  manualEntryLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 2,
  },
  manualEntryValue: {
    fontSize: 12,
  },
});

export default LocationPicker;
